import { useMemo, useRef } from "react";
import {
  QueryClient,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseQueries,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { withLoadDelay } from "../utils/loadDelay";
import {
  beginBatchLoad,
  captureStack,
  endBatchLoad,
  lookupDiagnosticsEnabled,
  reportLookupMiss,
} from "../utils/lookupDiagnostics";

/**
 * Generischer Batch-Cache.
 *
 * Eine Entität kann über mehrere "Indexe" adressiert werden (z. B. Food über
 * `foodId` und über `foodUnitId`; Purchase über `purchaseId` und `foodId`).
 * Alle `ensure`-Aufrufe innerhalb desselben Microtasks werden pro Index zu
 * einem Bulk-Request gebündelt. Fehlende Einträge werden – sofern ein
 * `loadOne` konfiguriert ist – per Single-Fetch nachgeladen.
 */
export type BatchIndex<T> = {
  /** Menschenlesbarer Name des Index (nur für Diagnose). */
  name?: string;
  /** Query-Key für einen Index-Eintrag. */
  key: (id: number) => readonly unknown[];
  /** Index-Ids, zu denen eine Entität gehört. */
  idsOf: (entity: T) => number[];
  /** Bulk-Loader für diesen Index. */
  load: (ids: number[]) => Promise<T[]>;
  /** Optionaler Single-Fetch-Fallback. */
  loadOne?: (id: number) => Promise<T>;
};

/** Diagnose-Kontext, der vom Hook bis in `ensure`/`get*` durchgereicht wird. */
type LookupContext = { renderStack?: string };

/** Anzahl je Index zwischengespeicherter "wo wurde der Lookup gebaut"-Traces. */
const MAX_BUILD_TRACES = 2000;

type BuildTrace = {
  stack?: string;
  keys: Record<string, unknown>;
};

export function createBatchCache<T extends { id: number }>(
  cacheName: string,
  indexes: BatchIndex<T>[],
) {
  const indexNames = indexes.map(
    (index, indexIdx) => index.name ?? `index${indexIdx}`,
  );
  const pending: Set<number>[] = indexes.map(() => new Set());
  const loadedIndexIds: Set<number>[] = indexes.map(() => new Set());
  const indexToEntityIds: Map<number, number[]>[] = indexes.map(
    () => new Map(),
  );
  const buildTraces: Map<number, BuildTrace>[] = indexes.map(() => new Map());
  let client: QueryClient | null = null;
  let scheduled = false;
  let waiters: { resolve: () => void; reject: (error: unknown) => void }[] = [];

  function rememberBuildTrace(
    indexIdx: number,
    id: number,
    keys: Record<string, unknown>,
    stack: string | undefined,
  ) {
    const traces = buildTraces[indexIdx];
    if (traces.has(id)) return;
    if (traces.size >= MAX_BUILD_TRACES) {
      const oldest = traces.keys().next().value;
      if (oldest !== undefined) traces.delete(oldest);
    }
    traces.set(id, { stack, keys });
  }

  function reportMiss(
    indexIdx: number,
    id: number,
    context?: LookupContext,
  ) {
    if (!lookupDiagnosticsEnabled() || !(id > 0)) return;
    const trace = buildTraces[indexIdx].get(id);
    void reportLookupMiss({
      kind: "batch-cache",
      cache: cacheName,
      index: indexNames[indexIdx],
      requestedId: id,
      keys: trace?.keys,
      buildStack: trace?.stack,
      accessStack: captureStack(),
      renderStack: context?.renderStack,
      knownIndexIds: [...indexToEntityIds[indexIdx].keys()].slice(0, 50),
    });
  }

  function seed(queryClient: QueryClient, entity: T) {
    client = queryClient;
    queryClient.setQueryData(indexes[0].key(entity.id), entity);
    indexes.forEach((index, indexIdx) => {
      index.idsOf(entity).forEach((indexId) => {
        const list = indexToEntityIds[indexIdx].get(indexId) ?? [];
        if (!list.includes(entity.id)) list.push(entity.id);
        indexToEntityIds[indexIdx].set(indexId, list);
      });
    });
  }

  async function flush() {
    const batches = pending.map((set) => [...set]);
    pending.forEach((set) => set.clear());
    scheduled = false;
    const done = waiters;
    waiters = [];
    try {
      const results = await Promise.all(
        indexes.map((index, indexIdx) =>
          batches[indexIdx].length
            ? withLoadDelay(index.load(batches[indexIdx]))
            : Promise.resolve([] as T[]),
        ),
      );
      results.flat().forEach((entity) => seed(client!, entity));

      // Fallback: Single-Fetch für angeforderte Ids ohne Bulk-Ergebnis.
      await Promise.all(
        indexes.map(async (index, indexIdx) => {
          if (!index.loadOne) return;
          const returned = new Set(results[indexIdx].map((e) => e.id));
          const missing = batches[indexIdx].filter((id) => !returned.has(id));
          if (!missing.length) return;
          const singles = await Promise.all(
            missing.map((id) => withLoadDelay(index.loadOne!(id))),
          );
          singles.forEach((entity) => seed(client!, entity));
        }),
      );

      batches.forEach((ids, indexIdx) =>
        ids.forEach((id) => loadedIndexIds[indexIdx].add(id)),
      );
      done.forEach((waiter) => waiter.resolve());
    } catch (error) {
      done.forEach((waiter) => waiter.reject(error));
    } finally {
      endBatchLoad();
    }
  }

  /**
   * `loadedIndexIds` hält eine Index-Id dauerhaft als geladen, während React
   * Query nicht (mehr) beobachtete Queries nach `gcTime` verwerfen kann. Liegt
   * zu einem bekannten Index-Eintrag keine Entity mehr im `queryData`, ist der
   * Eintrag veraltet und muss neu geladen werden – sonst liefert `getEntity`
   * dauerhaft `undefined` („Food nicht gefunden").
   *
   * Ein Eintrag ohne Entity-Zuordnung ist dagegen ein gültiges „geladen, aber
   * leer" (z. B. Food ohne Produkte/Units) und gilt nicht als veraltet.
   */
  function isStaleIndexEntry(
    queryClient: QueryClient,
    indexIdx: number,
    id: number,
  ): boolean {
    const entityIds = getEntityIds(indexIdx, id);
    if (entityIds.length === 0) return false;
    return !entityIds.some(
      (entityId) =>
        queryClient.getQueryData(indexes[0].key(entityId)) !== undefined,
    );
  }

  function ensure(
    queryClient: QueryClient,
    indexIdx: number,
    ids: number[],
  ): Promise<void> {
    client = queryClient;
    let needed = false;
    const diagnostics = lookupDiagnosticsEnabled();
    const buildStack = diagnostics ? captureStack() : undefined;
    ids.forEach((id) => {
      const loaded = loadedIndexIds[indexIdx].has(id);
      if (loaded && !isStaleIndexEntry(queryClient, indexIdx, id)) return;
      if (loaded) loadedIndexIds[indexIdx].delete(id);
      pending[indexIdx].add(id);
      needed = true;
      if (diagnostics && id > 0) {
        rememberBuildTrace(
          indexIdx,
          id,
          {
            cache: cacheName,
            index: indexNames[indexIdx],
            requestedIds: ids,
          },
          buildStack,
        );
      }
    });
    if (!needed) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      waiters.push({ resolve, reject });
      if (!scheduled) {
        scheduled = true;
        beginBatchLoad();
        queueMicrotask(flush);
      }
    });
  }

  function getEntity(
    queryClient: QueryClient,
    entityId: number,
    context?: LookupContext,
  ): T | undefined {
    const entity = queryClient.getQueryData<T>(indexes[0].key(entityId));
    if (entity == null) reportMiss(0, entityId, context);
    return entity;
  }

  function getEntityIds(indexIdx: number, id: number): number[] {
    return indexToEntityIds[indexIdx].get(id) ?? [];
  }

  function get(
    queryClient: QueryClient,
    indexIdx: number,
    id: number,
    context?: LookupContext,
  ): T | undefined {
    const entityId = getEntityIds(indexIdx, id)[0];
    if (entityId == null) {
      reportMiss(indexIdx, id, context);
      return undefined;
    }
    return getEntity(queryClient, entityId, context);
  }

  function getMany(
    queryClient: QueryClient,
    indexIdx: number,
    id: number,
  ): T[] {
    return getEntityIds(indexIdx, id)
      .map((entityId) => getEntity(queryClient, entityId))
      .filter((entity): entity is T => entity != null);
  }

  const EMPTY_ONE_KEY = ["__lookup__", "emptyOne"] as const;
  const EMPTY_MANY_KEY = ["__lookup__", "emptyMany"] as const;
  const hasId = (id: number | null | undefined): id is number =>
    id != null && id > 0;

  /**
   * Erfasst den Render-Stack einmal pro Hook-Instanz. Damit kann ein späterer
   * Miss die Komponente melden, die den Lookup verwendet hat – der Stack in der
   * `queryFn` zeigt sonst nur React-Query-Interna.
   */
  function useRenderStack(): LookupContext {
    const ref = useRef<LookupContext | null>(null);
    if (ref.current === null) {
      ref.current = { renderStack: captureStack() };
    }
    return ref.current;
  }

  /**
   * Stabilisiert die Referenz eines aus Query-Results abgeleiteten Wertes.
   * Solange sich Inhalt (Entity-Ids) und `dataUpdatedAt` nicht ändern, bleibt
   * die Referenz gleich – sonst triggern `useEffect`/`useMemo` in den
   * Konsumenten bei jedem Render.
   */
  function useStableBySignature<V>(value: V, signature: string): V {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => value, [signature]);
  }

  function idsSignature(data: unknown): string {
    if (Array.isArray(data)) {
      return (data as T[]).map((entity) => entity?.id).join(",");
    }
    return data && typeof data === "object"
      ? String((data as T).id)
      : "";
  }

  /**
   * Dedupliziert die Entities aus den Query-Results und stabilisiert die
   * Referenz über eine Signatur (Ids + `dataUpdatedAt`).
   */
  function useStableResultsEntities(
    results: { data?: unknown; dataUpdatedAt?: number }[],
  ): T[] {
    const signature = results
      .map(
        (result) => `${idsSignature(result.data)}@${result.dataUpdatedAt ?? 0}`,
      )
      .join("|");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => {
      const entities: T[] = [];
      results.forEach((result) => {
        const data = result.data;
        if (Array.isArray(data)) entities.push(...(data as T[]));
        else if (data) entities.push(data as T);
      });
      return [
        ...new Map(entities.map((entity) => [entity.id, entity])).values(),
      ];
    }, [signature]);
  }

  /**
   * Ein Query-Key pro Index hat **genau eine Datenform**:
   * - Index 0: einzelne Entity (`T | null`)
   * - Index > 0: Liste (`T[]`)
   *
   * Andernfalls würden `useOne` (Einzelwert) und `useByIds`/`useMany` (Liste)
   * denselben Key mit unterschiedlicher Form belegen – je nach Reihenfolge käme
   * dann z. B. aus `useFoodByUnit` plötzlich eine Liste.
   */
  function useIndexQuery(
    indexIdx: number,
    id: number | null | undefined,
  ): { data: T | T[] | null; dataUpdatedAt: number } {
    const queryClient = useQueryClient();
    const context = useRenderStack();
    const query = useSuspenseQuery({
      queryKey: hasId(id)
        ? indexes[indexIdx].key(id)
        : indexIdx === 0
          ? EMPTY_ONE_KEY
          : EMPTY_MANY_KEY,
      queryFn: async () => {
        if (!hasId(id)) return indexIdx === 0 ? null : [];
        await ensure(queryClient, indexIdx, [id]);
        return indexIdx === 0
          ? (getEntity(queryClient, id, context) ?? null)
          : getMany(queryClient, indexIdx, id);
      },
    });
    return {
      data: query.data as T | T[] | null,
      dataUpdatedAt: query.dataUpdatedAt,
    };
  }

  /**
   * Suspender Lookup einer einzelnen Entity über einen Index (z. B. Food über
   * `foodId` oder `foodUnitId`). Ist die Entity noch nicht im Cache, wird das
   * Fetch-Promise geworfen, sodass die nächsthöhere `QueryBoundary` den
   * Ladezustand zeigt.
   */
  function useOne(
    indexIdx: number,
    id: number | null | undefined,
  ): T | undefined {
    const { data } = useIndexQuery(indexIdx, id);
    if (indexIdx === 0) return (data as T | null) ?? undefined;
    return ((data as T[]) ?? [])[0];
  }

  /**
   * Suspender Lookup aller Entities zu einer Index-Id (z. B. alle Purchases
   * eines Foods). Liefert `[]`, wenn (noch) keine existieren.
   */
  function useMany(indexIdx: number, id: number | null | undefined): T[] {
    const { data, dataUpdatedAt } = useIndexQuery(indexIdx, id);
    const list: T[] =
      indexIdx === 0 ? (data ? [data as T] : []) : ((data as T[]) ?? []);
    return useStableBySignature(list, `${idsSignature(list)}@${dataUpdatedAt}`);
  }

  /** Non-suspending Variante von `useIndexQuery` (für zustandsbehaftete Container). */
  function useIndexQueryAsync(
    indexIdx: number,
    id: number | null | undefined,
  ): { data: T | T[] | null; dataUpdatedAt: number; isLoading: boolean } {
    const queryClient = useQueryClient();
    const context = useRenderStack();
    const query = useQuery({
      queryKey: hasId(id)
        ? indexes[indexIdx].key(id)
        : indexIdx === 0
          ? EMPTY_ONE_KEY
          : EMPTY_MANY_KEY,
      queryFn: async () => {
        if (!hasId(id)) return indexIdx === 0 ? null : [];
        await ensure(queryClient, indexIdx, [id]);
        return indexIdx === 0
          ? (getEntity(queryClient, id, context) ?? null)
          : getMany(queryClient, indexIdx, id);
      },
    });
    return {
      data:
        query.data === undefined
          ? indexIdx === 0
            ? null
            : []
          : (query.data as T | T[] | null),
      dataUpdatedAt: query.dataUpdatedAt,
      isLoading: query.isPending,
    };
  }

  function useOneAsync(
    indexIdx: number,
    id: number | null | undefined,
  ): T | undefined {
    const { data } = useIndexQueryAsync(indexIdx, id);
    if (indexIdx === 0) return (data as T | null) ?? undefined;
    return ((data as T[]) ?? [])[0];
  }

  function useManyAsync(
    indexIdx: number,
    id: number | null | undefined,
  ): T[] {
    const { data, dataUpdatedAt } = useIndexQueryAsync(indexIdx, id);
    const list: T[] =
      indexIdx === 0 ? (data ? [data as T] : []) : ((data as T[]) ?? []);
    return useStableBySignature(list, `${idsSignature(list)}@${dataUpdatedAt}`);
  }

  /**
   * Wie `useOneAsync`, gibt aber zusätzlich `isLoading` zurück. Damit können
   * Konsumenten „lädt noch" von „gibt es nicht" unterscheiden und einen
   * Ladeplatzhalter statt eines leeren Zustands rendern.
   */
  function useOneState(
    indexIdx: number,
    id: number | null | undefined,
  ): { data: T | undefined; isLoading: boolean } {
    const { data, isLoading } = useIndexQueryAsync(indexIdx, id);
    const value =
      indexIdx === 0 ? ((data as T | null) ?? undefined) : ((data as T[]) ?? [])[0];
    return { data: value, isLoading };
  }

  /** Wie `useManyAsync`, zusätzlich mit `isLoading`. */
  function useManyState(
    indexIdx: number,
    id: number | null | undefined,
  ): { data: T[]; isLoading: boolean } {
    const { data, dataUpdatedAt, isLoading } = useIndexQueryAsync(indexIdx, id);
    const list: T[] =
      indexIdx === 0 ? (data ? [data as T] : []) : ((data as T[]) ?? []);
    return {
      data: useStableBySignature(
        list,
        `${idsSignature(list)}@${dataUpdatedAt}`,
      ),
      isLoading,
    };
  }

  /**
   * Lädt die Entities für die übergebenen Index-Ids (pro Index ein Array).
   * Suspended, bis alle angefragten Einträge geladen sind.
   */
  function useByIds(idsPerIndex: number[][]): T[] {
    const queryClient = useQueryClient();
    const context = useRenderStack();
    const queries = idsPerIndex.flatMap((ids, indexIdx) =>
      [...new Set(ids)].map((id) =>
        indexIdx === 0
          ? {
              queryKey: indexes[0].key(id),
              queryFn: async () => {
                await ensure(queryClient, 0, [id]);
                return getEntity(queryClient, id, context) ?? null;
              },
            }
          : {
              queryKey: indexes[indexIdx].key(id),
              queryFn: async () => {
                await ensure(queryClient, indexIdx, [id]);
                return getMany(queryClient, indexIdx, id);
              },
            },
      ),
    );
    const results = useSuspenseQueries({ queries: queries as never[] });
    return useStableResultsEntities(results);
  }

  /**
   * Wie `useByIds`, aber **non-suspending**. Für zustandsbehaftete Container
   * (z. B. Seiten/Drawer mit State): einen Suspense dort würde den kompletten
   * Subtree (inkl. State) verwerfen und neu mounten → Flackern. Dort lieber
   * progressiv rendern.
   */
  function useByIdsAsync(idsPerIndex: number[][]): T[] {
    const queryClient = useQueryClient();
    const context = useRenderStack();
    const queries = idsPerIndex.flatMap((ids, indexIdx) =>
      [...new Set(ids)].map((id) =>
        indexIdx === 0
          ? {
              queryKey: indexes[0].key(id),
              queryFn: async () => {
                await ensure(queryClient, 0, [id]);
                return getEntity(queryClient, id, context) ?? null;
              },
            }
          : {
              queryKey: indexes[indexIdx].key(id),
              queryFn: async () => {
                await ensure(queryClient, indexIdx, [id]);
                return getMany(queryClient, indexIdx, id);
              },
            },
      ),
    );
    const results = useQueries({ queries: queries as never[] });
    return useStableResultsEntities(results);
  }

  /** Wie `useByIdsAsync`, zusätzlich mit `isLoading` (mind. eine Query pending). */
  function useByIdsState(idsPerIndex: number[][]): {
    data: T[];
    isLoading: boolean;
  } {
    const queryClient = useQueryClient();
    const context = useRenderStack();
    const queries = idsPerIndex.flatMap((ids, indexIdx) =>
      [...new Set(ids)].map((id) =>
        indexIdx === 0
          ? {
              queryKey: indexes[0].key(id),
              queryFn: async () => {
                await ensure(queryClient, 0, [id]);
                return getEntity(queryClient, id, context) ?? null;
              },
            }
          : {
              queryKey: indexes[indexIdx].key(id),
              queryFn: async () => {
                await ensure(queryClient, indexIdx, [id]);
                return getMany(queryClient, indexIdx, id);
              },
            },
      ),
    );
    const results = useQueries({ queries: queries as never[] });
    const isLoading = (results as unknown as { isPending?: boolean }[]).some(
      (result) => result.isPending,
    );
    return { data: useStableResultsEntities(results), isLoading };
  }

  /**
   * Verwirft die internen "schon geladen"-Markierungen und Index-Maps. Muss
   * aufgerufen werden, wenn die Server-Daten invalidiert wurden: `ensure` lädt
   * einen Index-Eintrag sonst nie erneut und `getMany` liefert dauerhaft den
   * (ggf. veralteten/leeren) Stand aus dem Speicher.
   */
  function reset() {
    loadedIndexIds.forEach((set) => set.clear());
    indexToEntityIds.forEach((map) => map.clear());
    buildTraces.forEach((map) => map.clear());
  }

  return {
    ensure,
    get,
    getMany,
    getEntity,
    seed,
    reset,
    useByIds,
    useByIdsAsync,
    useByIdsState,
    useOne,
    useMany,
    useOneAsync,
    useManyAsync,
    useOneState,
    useManyState,
  };
}
